import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

type State = "loading" | "valid" | "already" | "invalid" | "submitting" | "success" | "error";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [state, setState] = useState<State>("loading");

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    const validate = async () => {
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`;
        const res = await fetch(url, {
          headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
        });
        const data = await res.json();
        if (res.ok && data.valid) setState("valid");
        else if (data.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch {
        setState("invalid");
      }
    };
    validate();
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setState("submitting");
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
      body: { token },
    });
    if (error || !data?.success) setState("error");
    else setState("success");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="max-w-md w-full p-8 text-center space-y-6">
        <h1 className="text-2xl font-bold" style={{ color: "hsl(var(--primary))" }}>
          NutriMart<span style={{ color: "#3D4A3D" }}> RD</span>
        </h1>

        {state === "loading" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">Validando enlace...</p>
          </>
        )}

        {state === "valid" && (
          <>
            <h2 className="text-xl font-semibold">¿Cancelar suscripción?</h2>
            <p className="text-muted-foreground">
              Dejarás de recibir correos transaccionales y promocionales de NutriMart.
              Esto no afecta los correos esenciales de tu cuenta.
            </p>
            <Button onClick={handleConfirm} className="w-full" style={{ background: "hsl(var(--primary))" }}>
              Confirmar cancelación
            </Button>
          </>
        )}

        {state === "submitting" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">Procesando...</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "hsl(var(--primary))" }} />
            <h2 className="text-xl font-semibold">Suscripción cancelada</h2>
            <p className="text-muted-foreground">
              No recibirás más correos. Si cambias de opinión, contáctanos en cualquier momento.
            </p>
          </>
        )}

        {state === "already" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-muted-foreground" />
            <h2 className="text-xl font-semibold">Ya estás dado de baja</h2>
            <p className="text-muted-foreground">Esta dirección ya no recibe correos de NutriMart.</p>
          </>
        )}

        {(state === "invalid" || state === "error") && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h2 className="text-xl font-semibold">Enlace inválido</h2>
            <p className="text-muted-foreground">
              Este enlace ha expirado o no es válido. Si necesitas ayuda, responde a cualquiera
              de nuestros correos.
            </p>
          </>
        )}
      </Card>
    </div>
  );
};

export default Unsubscribe;
